import { NumberInput, Stack, Switch } from '@mantine/core'
import { IconTarget } from '@tabler/icons-react'
import { useMemo } from 'react'
import { z } from 'zod'
import {
  GRID_COLS,
  GRID_ROWS,
  GridPosition,
  SLIDE_CONTENT_HEIGHT,
  SLIDE_CONTENT_WIDTH,
} from '../types'
import { Metric, useMetrics, useMetricsData } from '../../../../widgets/api'
import { useFmt, useFmtColor } from '../../../../widgets/format'
import { DashboardWidget } from './types'
import { useResolvedContext } from './useResolvedContext'
import {
  ColorSchemaFields,
  ContextFields,
  ContextSchemaFields,
  MetricSelect,
  NeutralColorsToggle,
  TitleInput,
} from './OptionsFormFields'
import { WidgetEmpty, WidgetSuspense } from './WidgetShell'
import classes from './TargetProgress.module.css'

const GAP_PX = 32
const CELL_W = (SLIDE_CONTENT_WIDTH - (GRID_COLS - 1) * GAP_PX) / GRID_COLS
const CELL_H = (SLIDE_CONTENT_HEIGHT - (GRID_ROWS - 1) * GAP_PX) / GRID_ROWS

const Schema = z.object({
  ...ContextSchemaFields,
  ...ColorSchemaFields,
  metric_key: z.string().default(''),
  title_top: z.string().default('Remaining to Target'),
  title_bottom: z.string().default(''),
  manual: z.boolean().default(false),
  manual_target: z.number().default(0),
  manual_actual: z.number().default(0),
  override_target: z.boolean().default(false),
  override_target_value: z.number().default(0),
})

type Settings = z.infer<typeof Schema>

type Sizes = {
  padding: number
  gap: number
  title: number
  bigNumber: number
  number: number
  barHeight: number
  barPct: number
  scale: number
}

function computeSizes(position: GridPosition): Sizes {
  const boxW = position.colSpan * CELL_W + (position.colSpan - 1) * GAP_PX
  const boxH = position.rowSpan * CELL_H + (position.rowSpan - 1) * GAP_PX
  const padding = Math.round(Math.min(boxW, boxH) * 0.045)
  const availW = boxW - padding * 2
  const availH = boxH - padding * 2

  const bigNumber = Math.min(availW * 0.15, availH * 0.3)
  return {
    padding,
    gap: Math.round(availH * 0.025),
    title: Math.round(Math.min(availW * 0.035, availH * 0.075)),
    bigNumber: Math.round(bigNumber),
    number: Math.round(bigNumber * 0.5),
    barHeight: Math.round(availH * 0.16),
    barPct: Math.round(availH * 0.16 * 0.55),
    scale: Math.round(Math.min(availW * 0.024, availH * 0.055)),
  }
}

function compactNumber(n: number): string {
  const fmt1 = (v: number) => {
    const s = v.toFixed(1)
    return s.endsWith('.0') ? s.slice(0, -2) : s
  }
  const abs = Math.abs(n)
  if (abs >= 1e9) return fmt1(n / 1e9) + 'b'
  if (abs >= 1e6) return fmt1(n / 1e6) + 'm'
  if (abs >= 1e3) return fmt1(n / 1e3) + 'k'
  return String(Math.round(n))
}

/** Formats an integer-ish value with the metric's config, or plain grouping. */
function NumberValue({
  value,
  metricKey,
}: {
  value: number
  metricKey: string
}) {
  const formatted = useFmt({
    metric: metricKey,
    component: 'actual',
    statistic: 'value',
    value,
  })
  if (!metricKey) return <>{new Intl.NumberFormat('en-US').format(value)}</>
  return <>{formatted}</>
}

function Display({
  target,
  actual,
  percent,
  metricKey,
  titleTop,
  titleBottom,
  position,
  neutral,
}: {
  target: number
  actual: number
  percent: number
  metricKey: string
  titleTop: string
  titleBottom: string
  position: GridPosition
  neutral: boolean
}) {
  const sizes = useMemo(() => computeSizes(position), [position])
  const remaining = Math.max(0, target - actual)
  const fillPct = Math.min(100, Math.max(0, percent * 100))

  const metricColor = useFmtColor({
    metric: metricKey,
    component: 'percent',
    statistic: 'value',
    value: percent,
  })
  const barColor = (!neutral && metricColor) || 'var(--mantine-color-teal-5)'

  const percentFmt = useFmt({
    metric: metricKey,
    component: 'percent',
    statistic: 'value',
    value: percent,
  })
  const percentLabel = metricKey ? percentFmt : `${(percent * 100).toFixed(1)}%`

  return (
    <div
      className={classes.box}
      style={{ padding: sizes.padding, gap: sizes.gap }}
    >
      <div className={classes.titleTop} style={{ fontSize: sizes.title }}>
        {titleTop}
      </div>
      <div className={classes.bigNumber} style={{ fontSize: sizes.bigNumber }}>
        <NumberValue value={remaining} metricKey={metricKey} />
      </div>
      <div className={classes.titleBottom} style={{ fontSize: sizes.title }}>
        {titleBottom}
      </div>
      <div className={classes.number} style={{ fontSize: sizes.number }}>
        <NumberValue value={actual} metricKey={metricKey} />
      </div>
      <div className={classes.barTrack} style={{ height: sizes.barHeight }}>
        <div
          className={classes.barFill}
          style={{ width: `${fillPct}%`, backgroundColor: barColor }}
        >
          {fillPct >= 20 && (
            <span className={classes.barPct} style={{ fontSize: sizes.barPct }}>
              {percentLabel}
            </span>
          )}
        </div>
        {fillPct < 20 && (
          <span
            className={classes.barPctOutside}
            style={{ fontSize: sizes.barPct, color: barColor }}
          >
            {percentLabel}
          </span>
        )}
      </div>
      <div className={classes.scaleRow}>
        <span className={classes.scaleMin} style={{ fontSize: sizes.scale }}>
          0
        </span>
        <span className={classes.scaleMax} style={{ fontSize: sizes.scale }}>
          {compactNumber(target)}
        </span>
      </div>
    </div>
  )
}

function SystemView({
  metric,
  settings,
  position,
  titleTop,
  titleBottom,
}: {
  metric: Metric
  settings: Settings
  position: GridPosition
  titleTop: string
  titleBottom: string
}) {
  const ctx = useResolvedContext(settings)
  const base = {
    period_type: ctx.period_type,
    period_range: false,
    periods: [ctx.period],
    subject_type: ctx.subject_type,
    subject_ids: [ctx.subject_id],
    metrics: [metric.key],
    statistics: ['value'],
    competition_id: ctx.competition_id,
  }
  const { data: targetPts } = useMetricsData({
    ...base,
    components: ['target'],
  })
  const { data: actualPts } = useMetricsData({
    ...base,
    components: ['actual'],
  })
  const { data: percentPts } = useMetricsData({
    ...base,
    components: ['percent'],
  })

  const overrideTarget = settings.override_target
  const systemActual = actualPts[0]?.value ?? 0
  const target = overrideTarget
    ? settings.override_target_value
    : (targetPts[0]?.value ?? 0)
  const percent = overrideTarget
    ? target > 0
      ? systemActual / target
      : 0
    : (percentPts[0]?.value ?? 0)

  return (
    <Display
      target={target}
      actual={systemActual}
      percent={percent}
      metricKey={metric.key}
      titleTop={titleTop}
      titleBottom={titleBottom}
      position={position}
      neutral={settings.use_neutral_colors}
    />
  )
}

function Inner({
  settings,
  position,
}: {
  settings: Settings
  position: GridPosition
}) {
  const { data: allMetrics } = useMetrics()
  const metric = allMetrics.find((m) => m.key === settings.metric_key)
  const titleTop = settings.title_top || 'Remaining to Target'
  const titleBottom = settings.title_bottom || metric?.name || 'Actual'

  if (settings.manual) {
    const target = settings.manual_target
    const actual = settings.manual_actual
    const percent = target > 0 ? actual / target : 0
    return (
      <Display
        target={target}
        actual={actual}
        percent={percent}
        metricKey={metric?.key ?? ''}
        titleTop={titleTop}
        titleBottom={titleBottom}
        position={position}
        neutral={settings.use_neutral_colors}
      />
    )
  }

  if (!metric) return <WidgetEmpty message="Pick a KPI" />
  return (
    <SystemView
      metric={metric}
      settings={settings}
      position={position}
      titleTop={titleTop}
      titleBottom={titleBottom}
    />
  )
}

function Component({
  settings,
  position,
}: {
  settings: Settings
  position: GridPosition
}) {
  return (
    <WidgetSuspense>
      <Inner settings={settings} position={position} />
    </WidgetSuspense>
  )
}

function OptionsForm({
  value,
  onChange,
}: {
  value: Settings
  onChange: (next: Settings) => void
}) {
  const patch = (p: Partial<Settings>) => onChange({ ...value, ...p })
  return (
    <Stack gap="xs">
      <TitleInput
        label="Top label"
        placeholder="Remaining to Target"
        value={value.title_top}
        onChange={(v) => patch({ title_top: v })}
      />
      <TitleInput
        label="Bottom label"
        placeholder="Metric name"
        value={value.title_bottom}
        onChange={(v) => patch({ title_bottom: v })}
      />
      <MetricSelect
        kpiOnly
        value={value.metric_key}
        onChange={(v) => patch({ metric_key: v })}
      />
      <Switch
        label="Manual values"
        checked={value.manual}
        onChange={(e) => patch({ manual: e.currentTarget.checked })}
      />
      {value.manual ? (
        <>
          <NumberInput
            label="Target"
            value={value.manual_target}
            onChange={(v) =>
              patch({ manual_target: typeof v === 'number' ? v : 0 })
            }
            thousandSeparator
          />
          <NumberInput
            label="Actual"
            value={value.manual_actual}
            onChange={(v) =>
              patch({ manual_actual: typeof v === 'number' ? v : 0 })
            }
            thousandSeparator
          />
        </>
      ) : (
        <>
          <ContextFields
            subject_type={value.subject_type}
            subject_id={value.subject_id}
            period_type={value.period_type}
            period={value.period}
            use_max_period={value.use_max_period}
            onChange={(p) => patch(p as Partial<Settings>)}
          />
          <Switch
            label="Override target"
            checked={value.override_target}
            onChange={(e) =>
              patch({ override_target: e.currentTarget.checked })
            }
          />
          {value.override_target && (
            <NumberInput
              label="Target"
              value={value.override_target_value}
              onChange={(v) =>
                patch({ override_target_value: typeof v === 'number' ? v : 0 })
              }
              thousandSeparator
            />
          )}
        </>
      )}
      <NeutralColorsToggle
        value={value.use_neutral_colors}
        onChange={(v) => patch({ use_neutral_colors: v })}
      />
    </Stack>
  )
}

export const TargetProgressWidget: DashboardWidget<Settings> = {
  type: 'target_progress',
  label: 'Target Progress',
  description: 'Remaining-to-target, actual, and a percent progress bar',
  icon: IconTarget,
  minW: 3,
  minH: 2,
  maxW: 6,
  maxH: 3,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  Component,
  OptionsForm,
}
